import { capitalize, useMediaQuery, useTheme } from "@mui/material";
import { IMatchupsResponse, IOptimizerState, IParlayOutcome, IShopOffer, IShopOutcome, IShopOutcomesPagination, IState, IToolFilters, Leagues, Operators, PickemParlayType } from "../../pick6/store/types";
import { APIState } from "../../utils/api/reducer";
import { memoize } from 'lodash';
import forEach from 'lodash/forEach';
import lowerCase from 'lodash/lowerCase';

export interface IToolsState {
  sportsbookCodes: string[];
  operator: Operators;
  league: Leagues;
  userSelectedSportsbookCodes: string[];
  readonly parlaysData: APIState<IParlayOutcome[]>;
  readonly matchupsByLeague: APIState<IMatchupsResponse[]>;
  wager: number;
  readonly arbitrageWager: string;
  readonly timeZone: string;
  readonly projectionSystem: string;
  readonly maxHold: number;
  readonly additionalLegs: APIState<{
    outcomes: IShopOutcome[];
    pagination: Partial<IShopOutcomesPagination>;
  }>;
  readonly oddsBoostAdditionalLegs: APIState<{
    outcomes: IShopOutcome[];
    pagination: Partial<IShopOutcomesPagination>;
  }>;
  parlayFilters: IParlayFilters;
  additionalParlayLegFilters: IAdditionalParlayLegsFilters;
  selectedLegs: IParlayOutcome[];
  oddsBoostSelectedLegs: IShopOutcome[];
  readonly state: IState;
  arbitrageLoading: boolean;
  filters: IToolFilters;
  optimizerState: IOptimizerState;
  pickemParlayType: PickemParlayType;
  timeframe: string;
  sportsbook: string;
  shopOddsStates?: IState[];
  offers: IOffers;
  description: string;
}

export interface IParlayFilters {
  projectionSystem: string;
  league: Leagues;
  parlayType?: string;
  sportsbook?: SportsbookCodes;
  legs?: number;
  minOdds?: string;
  operator?: Operators;
  pickemParlayType?: PickemParlayType
  timeframe: string;
  state?: IState;
}

export interface IAdditionalParlayLegsFilters {
  events?: string[];
  league: Leagues;
  betTypes?: IShopOffer[];
}

export enum SportsbookCodes {
  WILLIAMHILL = 'Caesars',
  SUGARHOUSE = 'SugarHouse',
  POINTSBET = 'PointsBet',
  FANDUEL = 'FanDuel',
  BETMGM = 'BetMGM',
  DRAFTKINGS = 'DraftKings',
  BETRIVERS = 'BetRivers',
  CAESARS = 'Caesars',
  WYNNBET = 'WynnBET',
  UNIBET = 'Unibet',
  PRIZEPICKS = 'PrizePicks',
  BET365 = 'Bet365',
  UNDERDOG = 'underdog',
  SLEEPER = 'Sleeper',
  ESPNBET = 'ESPNBet',
  BETR = 'Betr',
  FANATICS = 'Fanatics',
  HARDROCK = 'HardRock',
  CIRCA = 'Circa',
  PINNACLE = 'Pinnacle',
  FLIFF = 'Fliff',
  DABBLE = 'Dabble',
  BETONLINE = 'BetOnline',
  PICK6 = 'Pick6',
  TRUEODDS = 'TrueOdds',
  ALL = '',
}

export enum OperatorsMinOdds {
  DEFAULT = '+1000',
  SLEEPER = '+100'
}

export enum OperatorTypes {
  PICKEM = 'pickem',
  SPORTSBOOK = 'sportsbook',
}

export const legCountAndPickemTypeToOddsDictionary: {
  [string: string]: number;
} = {
  'PrizePicks|Flex|3': 59.09,
  'PrizePicks|Flex|4': 56.89,
  'PrizePicks|Flex|5': 54.25,
  'PrizePicks|Flex|6': 54.21,
  'PrizePicks|Power|2': 57.74,
  'PrizePicks|Power|3': 58.48,
  'PrizePicks|Power|4': 56.23,
  'NoHouseAdvantage|Power|2': 55.90,
  'NoHouseAdvantage|Power|3': 55.03,
  'NoHouseAdvantage|Power|4': 54.91,
  'NoHouseAdvantage|Power|5': 54.39,
  'NoHouseAdvantage|Flex|4': 54.49,
  'NoHouseAdvantage|Flex|5': 53.22,
  'Underdog|Power|2': 57.74,
  'Underdog|Power|3': 55.03,
  'Underdog|Power|4': 56.23,
  'Underdog|Power|5': 54.93,
  'Underdog|Flex|3': 57.74,
  'Underdog|Flex|4': 55.03,
  'Underdog|Flex|5': 54.75,
  'Betr|Power|2': 57.80,
  'Betr|Power|3': 58.50,
  'Betr|Power|4': 56.30,
  'Betr|Power|5': 55.00,
  'Betr|Power|6': 56.70,
  'Betr|Power|7': 57.30,
  'Betr|Power|8': 56.30,
  'Dabble|Power|2': 57.60,
  'Dabble|Power|3': 55.00,
  'Dabble|Power|4': 56.10,
  'Dabble|Power|5': 54.80,
  'Dabble|Power|6': 55.40,
  'Dabble|Power|7': 55.80,
  'Dabble|Power|8': 56.30,
  'Dabble|Flex|3': 59.00,
  'Dabble|Flex|4': 56.90,
  'Dabble|Flex|5': 55.80,
  'Dabble|Flex|6': 55.00,
  'Dabble|Flex|7': 56.30,
  'Dabble|Flex|8': 56.60,
  'Pick6|Flex|2': 64.55,
  'Pick6|Flex|3': 61.78,
  'Pick6|Flex|4': 58.84,
  'Pick6|Flex|5': 58.82,
  'Pick6|Flex|6': 57.72,
};

export const GetPickemParlayTypeLabel = memoize((type: PickemParlayType, operator: Operators): string => {
  switch (operator) {
    case Operators.UNDERDOG:
      return type === PickemParlayType.FLEX ? 'Insured' : 'Standard';
    default:
      return type;
  }
});

export const GetValidPickemParlayTypesForOperator = memoize(
  (operator?: Operators): PickemParlayType[] => {
    switch (operator) {
      case Operators.UNDERDOG:
        return [PickemParlayType.POWER, PickemParlayType.FLEX];
      case Operators.PRIZEPICKS:
        return [PickemParlayType.FLEX, PickemParlayType.POWER];
      case Operators.PICK6:
        return [PickemParlayType.FLEX];
      default:
        return [];
    }
  },
);


export const SportsbookBgColors: { [key: string]: string } = {
  DK: '#9ac434',
  FD: '#1493ff',
  MGM: '#000000',
  PB: '#000000',
  BT: '#027b5b',
  BR: '#21355b',
  Bet888: '#ff761f',
  WH: '#173432',
  SH: '#256091',
  CZR: '#173432',
  WB: '#091633',
  UB: '#3EAB37',
  ESPN: '#00122A',
  BETR: '#2DEDE6',
  FT: '#091F41',
  HR: '#1A181B',
  CR: '#121495',
  PN: '##1c1c1c80',
  FF: '#141445',
  DB: '#7E4BFF',
  JM: '#09B27E',
  SL: '##282F54',
  PP: '#000000',
  UD: '#FFF',
  P6: '#F6BD06',
  DEFAULT: '#000000',
};

export const getSportsbookBgColor = memoize(
  (sb: string) => SportsbookBgColors[getSportsbookCode(sb)],
);

export const getSportsbookCode = memoize((sportsbookCode?: string) => {
  if (!sportsbookCode) {
    return '';
  }
  let code: string = '';
  switch (getSportsbookCodeLabel(sportsbookCode?.toUpperCase())) {
    case SportsbookCodes.WILLIAMHILL:
      code = 'WH';
      break;
    case SportsbookCodes.SUGARHOUSE:
      code = 'SH';
      break;
    case SportsbookCodes.POINTSBET:
      code = 'PB';
      break;
    case SportsbookCodes.FANDUEL:
      code = 'FD';
      break;
    case SportsbookCodes.BETMGM:
      code = 'MGM';
      break;
    case SportsbookCodes.DRAFTKINGS:
      code = 'DK';
      break;
    case SportsbookCodes.BETRIVERS:
      code = 'BR';
      break;
    case SportsbookCodes.CAESARS:
      code = 'CZR';
      break;
    case SportsbookCodes.WYNNBET:
      code = 'WB';
      break;
    case SportsbookCodes.UNIBET:
      code = 'UB';
      break;
    case SportsbookCodes.BET365:
      code = 'BT';
      break;
    case SportsbookCodes.PRIZEPICKS:
      code = 'PP';
      break;
    case SportsbookCodes.UNDERDOG:
      code = 'UD';
      break;
    case SportsbookCodes.SLEEPER:
      code = 'SL';
      break;
    case SportsbookCodes.ESPNBET:
      code = 'ESPN';
      break;
    case SportsbookCodes.BETR:
      code = 'BETR';
      break;
    case SportsbookCodes.FANATICS:
      code = 'FT';
      break;
    case SportsbookCodes.PINNACLE:
      code = 'PN';
      break;
    case SportsbookCodes.HARDROCK:
      code = 'HR';
      break;
    case SportsbookCodes.CIRCA:
      code = 'CR';
      break;
    case SportsbookCodes.FLIFF:
      code = 'FF';
      break;
    case SportsbookCodes.DABBLE:
      code = 'DB';
      break;
    case SportsbookCodes.PICK6:
      code = 'P6';
      break;
    case SportsbookCodes.TRUEODDS:
      code = 'TO';
      break;
    case SportsbookCodes.BETONLINE:
      code = 'BO';
      break;
    default:
      code = 'DEFAULT';
      break;
  }
  return code;
});

export const getSportsbookCodeLabel = memoize((sportsbookCode: string) => {
  let code: string = '';
  switch (sportsbookCode?.toUpperCase()) {
    case 'WILLIAMHILL':
      code = SportsbookCodes.WILLIAMHILL;
      break;
    case 'SUGARHOUSE':
      code = SportsbookCodes.SUGARHOUSE;
      break;
    case 'POINTSBET':
      code = SportsbookCodes.POINTSBET;
      break;
    case 'FANDUEL':
      code = SportsbookCodes.FANDUEL;
      break;
    case 'BETMGM':
      code = SportsbookCodes.BETMGM;
      break;
    case 'DRAFTKINGS':
      code = SportsbookCodes.DRAFTKINGS;
      break;
    case 'BETRIVERS':
      code = SportsbookCodes.BETRIVERS;
      break;
    case 'CAESARS':
      code = SportsbookCodes.CAESARS;
      break;
    case 'WYNNBET':
      code = SportsbookCodes.WYNNBET;
      break;
    case 'UNIBET':
      code = SportsbookCodes.UNIBET;
      break;
    case 'PRIZEPICKS':
      code = SportsbookCodes.PRIZEPICKS;
      break;
    case 'BET365':
      code = SportsbookCodes.BET365;
      break;
    case 'UNDERDOG':
      code = SportsbookCodes.UNDERDOG;
      break;
    case 'SLEEPER':
      code = SportsbookCodes.SLEEPER;
      break;
    case 'ESPNBET':
      code = SportsbookCodes.ESPNBET;
      break;
    case 'BETR':
      code = SportsbookCodes.BETR;
      break;
    case 'FANATICS':
      code = SportsbookCodes.FANATICS;
      break;
    case 'PINNACLE':
      code = SportsbookCodes.PINNACLE;
      break;
    case 'HARDROCK':
      code = SportsbookCodes.HARDROCK;
      break;
    case 'CIRCA':
      code = SportsbookCodes.CIRCA;
      break;
    case 'FLIFF':
      code = SportsbookCodes.FLIFF;
      break;
    case 'DABBLE':
      code = SportsbookCodes.DABBLE;
      break;
    case 'PICK6':
      code = SportsbookCodes.PICK6;
      break;
    case 'TRUEODDS':
      code = SportsbookCodes.TRUEODDS;
      break;
    case 'BETONLINE':
      code = SportsbookCodes.BETONLINE;
      break;
    default:
      code = SportsbookCodes.ALL;
      break;
  }
  return code;
});

export const GetOperatorLabel = memoize((operator?: Operators) => {
  switch (operator) {
    case Operators.PRIZEPICKS:
      return 'PrizePicks';
    case Operators.UNDERDOG:
      return 'Underdog';
    case Operators.PICK6:
      return 'Pick6';
    default:
      return '';
  }
});

export const GetOperatorLink = memoize((operator?: Operators) => {
  switch (operator) {
    case Operators.PRIZEPICKS:
      return 'prizepicks';
    case Operators.UNDERDOG:
      return 'underdog';
    case Operators.PICK6:
      return 'pick6';
    default:
      return '';
  }
});

export enum Layout {
  Desktop,
  MobileWeb,
  MobileApp
}

export const GetLayout = (): Layout => {
  const theme = useTheme();
  const mdSize = useMediaQuery(theme.breakpoints.down('md'));
  if (mdSize) {
    return Layout.MobileApp;
  }
  // Currently we don't support out mobile layout above md size. Make sure to show the desktop for this 
  // because certain elements will be hidden for MobileApp layout but since everything is based on md size
  // it will look like the desktop regardless but be missing elements in the headers.
  if (!mdSize) {
    return Layout.Desktop;
  }
  if (mdSize) {
    return Layout.MobileWeb;
  }

  return Layout.Desktop;
}

export const IsOperatorPeer2Peer = memoize((operator?: string) => {
  switch (operator?.toUpperCase()) {
    case 'PICK6':
      return true;
    default:
      return false;
  }
});

export const formatToUSDollarFloat = (value: any = 0): string => {
  const number: number = parseFloat(value);
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(number);
};

export interface IMatchedParlay {
  oddsAmerican: string;
  xEv: number;
  xWin: string;
  sportsbookCode: string;
  hashCodes: string[];
}

export const getParlayDeepLink = (outcomes: IParlayOutcome[] | IShopOutcome[]) => {
  if (!outcomes.length) {
    return '';
  }
  var url = '';
  switch (outcomes[0].sportsbookCode.toLowerCase()) {
    case 'prizepicks':
      url = PrizePicksParlayLink(outcomes);
      break;
    case 'draftkings':
      url = DraftKingsParlayLink(outcomes);
      break;
    case 'caesars':
      url = CaesarsParlayLink(outcomes);
      break;
    case 'betmgm':
      url = BetMGMParlayLink(outcomes);
      break;
    case 'betrivers':
      url = BetRiversParlayLink(outcomes);
      break;
    case 'bet365':
      url = Bet365ParlayLink(outcomes);
      break;
    default:
      url = outcomes[0].deepLinkUrl
      break;
  }
  return url;
};

const PrizePicksParlayLink = (outcomes: IParlayOutcome[] | IShopOutcome[]) => {
  var url = outcomes[0].deepLinkUrl.split('&projections=')[0] + '&projections=';
  var outcomeIds = [] as string[];
  outcomes.forEach((outcome) => {
    let outcomeId = outcome.deepLinkUrl.split('&projections=')[1];
    outcomeIds.push(outcomeId);
  });
  url = url + outcomeIds.join(',');
  return url;
};

const DraftKingsParlayLink = (outcomes: IParlayOutcome[] | IShopOutcome[]) => {
  var url = outcomes[0].deepLinkUrl.split('?outcomes=')[0] + '?outcomes=';
  var outcomeIds = [] as string[];
  outcomes.forEach((outcome) => {
    let firstParse = outcome.deepLinkUrl.split('?outcomes=')[1];
    let outcomeId = firstParse.split('&')[0];
    outcomeIds.push(outcomeId);
  });
  url = url + outcomeIds.join(' | ');
  return url;
};

const CaesarsParlayLink = (outcomes: IParlayOutcome[] | IShopOutcome[]) => {
  var url = outcomes[0].deepLinkUrl.split('?selectionIds=')[0] + '?selectionIds=';
  var outcomeIds = [] as string[];
  outcomes.forEach((outcome) => {
    let firstParse = outcome.deepLinkUrl.split('?selectionIds=')[1];
    let outcomeId = firstParse.split('&')[0];
    outcomeIds.push(outcomeId);
  });
  url = url + outcomeIds.join(',');
  return url;
};

const BetMGMParlayLink = (outcomes: IParlayOutcome[] | IShopOutcome[]) => {
  var url = outcomes[0].deepLinkUrl.split('?options=')[0] + '?options=';
  var outcomeIds = [] as string[];
  outcomes.forEach((outcome) => {
    let firstParse = outcome.deepLinkUrl.split('?options=')[1];
    let outcomeId = firstParse.split('&')[0];
    outcomeIds.push(outcomeId);
  });
  url = url + outcomeIds.join(',');
  return url;
};

const BetRiversParlayLink = (outcomes: IParlayOutcome[] | IShopOutcome[]) => {
  var url = outcomes[0].deepLinkUrl.split('?coupon=single')[0] + '?coupon=combination';
  var outcomeIds = [] as string[];
  outcomes.forEach((outcome) => {
    let outcomeId = outcome.deepLinkUrl.split('|')[1];
    outcomeIds.push(outcomeId);
  });
  url = url + '|' + outcomeIds.join(',') + '|';
  return url;
};

const Bet365ParlayLink = (outcomes: IParlayOutcome[] | IShopOutcome[]) => {
  var url = outcomes[0].deepLinkUrl.split('?bs=')[0] + '?bs=';
  var outcomeIds = [] as string[];
  outcomes.forEach((outcome) => {
    let firstParse = outcome.deepLinkUrl.split('?bs=')[1];
    let outcomeId = firstParse.split('&')[0];
    outcomeIds.push(outcomeId);
  });
  url = url + outcomeIds.join('|') + '&bet=1';
  return url;
};
export const LeagueDisplayText = (league: string): string => {
  if (league?.toUpperCase() !== 'GOLF'
    && league?.toUpperCase() !== 'TENNIS') {
    return league?.toUpperCase();
  }
  return capitalize(league);
}

export const filterShopOfferCategoriesForPickEm = (
  offers: IShopOffer[],
) => {
  var filteredOffers = offers.filter(
    (x =>
      (x.offerCategory.toUpperCase() === 'PLAYERPROPS' || x.leagueCode?.toUpperCase() === 'TENNIS')
    )
  );

  return filteredOffers;
};

export const leaguesSorted: Leagues[] = [
  Leagues.NFL,
  Leagues.NBA,
  Leagues.MLB,
  Leagues.WNBA,
  Leagues.NCAAF,
  Leagues.NCAAB,
  Leagues.NHL,
  Leagues.UFC,
  Leagues.EPL,
  Leagues.GOLF,
  Leagues.TENNIS,
];

export interface IShopOfferCategory {
  offerCategory: string;
  displayName: string;
  offerType: OfferTypes;
  offers: IShopOffer[];
}

export enum OfferTypes {
  GAMELINES = 'GAMELINES',
  TOURNAMENTLINES = 'TOURNAMENTLINES',
  GAMEPROPS = 'GAMEPROPS',
  TEAM_PROPS = 'TEAMPROPS',
  PLAYER_PROPS = 'PLAYERPROPS',
  FUTURES = 'FUTURES',
  PLAYER_FUTURES = 'PLAYERFUTURES',
  TEAM_FUTURES = 'TEAMFUTURES',
  CONFERENCE_FUTURES = 'CONFERENCEFUTURES',
  DIVISION_FUTURES = 'DIVISIONFUTURES',
  PLAYER_MATCHUPS = 'PLAYERMATCHUPS',
  UNKNOWN = 'UNKNOWN',
}

export interface IOffers {
  [key: string]: IShopOfferCategory;
}

export interface IOffersResponse {
  offerCategories: IOfferCategory[];
}
export interface IOfferCategory {
  readonly name: string;
  readonly offers: IShopOffer[]
}

export const getFormattedOffersBeta = (
  data: IOffersResponse,
  league: string,
  state: string,
) => {
  const { offerCategories } = data;

  const offersObj: IOffers = {};

  const updateOffersObj = (
    key: string,
    displayName: string,
    offerCategory: string,
    offer: IShopOffer,
  ): void => {
    offer = {
      ...offer,
      uri: `/odds/${state}/${lowerCase(league)}/${lowerCase(
        displayName,
      ).replace(/\s/g, '')}/${lowerCase(offer.name).replace(/\s/g, '')}/${offer.id
        }`,
    };
    if (!offersObj[key]) {
      offersObj[key] = {
        offerCategory: offerCategory?.toUpperCase(),
        offerType: getOfferType(key),
        displayName,
        offers: [
          {
            ...offer,
          },
        ],
      };
    } else {
      offersObj[key]?.offers?.push({
        ...offer,
      });
    }
  };

  forEach(offerCategories, (oc: IOfferCategory) => {
    const { offers, name } = oc;
    forEach(offers, (o: IShopOffer) => {
      switch (o.offerCategory?.toUpperCase()) {
        case 'GAMELINES':
          updateOffersObj('gamelines', 'Game Lines', name, o);
          break;
        case 'GAMEPROPS':
          updateOffersObj('gameprops', 'Game Props', name, o);
          break;
        case 'TEAMPROPS':
          updateOffersObj('teamprops', 'Team Props', name, o);
          break;
        case 'PLAYERPROPS':
          updateOffersObj('playerprops', 'Player Props', name, o);
          break;
        case 'PLAYERFUTURES':
          updateOffersObj('playerfutures', 'Player Futures', name, o);
          break;
        case 'TEAMFUTURES':
          updateOffersObj('teamfutures', 'Team Futures', name, o);
          break;
        case 'PLAYERMATCHUPS': // Golf Integration
          updateOffersObj('playermatchups', 'Player Matchups', name, o);
          break;
        case 'TOURNAMENTLINES': // Golf Integration
          updateOffersObj('tournamentlines', 'Tournament Lines', name, o);
          break;
        case 'FUTURES': // Golf Integration
          updateOffersObj('futures', 'Tournament Futures', name, o);
          break;
        default:
          break;
      }
    });
  });

  return offersObj;
};

export const getOfferType = (offerType: string): OfferTypes => {
  const ot = offerType?.toLowerCase();
  switch (ot) {
    case 'gamelines':
    case 'fightlines':
    case 'pucklines':
    case 'runlines':
      return OfferTypes.GAMELINES;
    case 'tournamentlines':
      return OfferTypes.TOURNAMENTLINES;
    case 'gameprops':
      return OfferTypes.GAMEPROPS;
    case 'teamprops':
      return OfferTypes.TEAM_PROPS;
    case 'playerprops':
      return OfferTypes.PLAYER_PROPS;
    case 'playerfutures':
    case 'oddstowin':
    case 'finishingposition':
      return OfferTypes.PLAYER_FUTURES;
    case 'teamfutures':
      return OfferTypes.TEAM_FUTURES;
    case 'divisionfutures':
      return OfferTypes.DIVISION_FUTURES;
    case 'conferencefutures':
      return OfferTypes.CONFERENCE_FUTURES;
    case 'playermatchups':
      return OfferTypes.PLAYER_MATCHUPS;
    case 'futures':
      return OfferTypes.FUTURES;
    default:
      return OfferTypes.UNKNOWN;
  }
};

export const underDogDescription = `<h2>Underdog Entry Builder</h2> <p><a href="https://play.underdogfantasy.com/pc-HpdqgpHQ6y" rel="nofollow" target="_new">Underdog Fantasy</a> has something for everyone. They are best known for their industry-leading NFL best ball contests with prizes as high as $2 million to first place. <a href="https://play.underdogfantasy.com/pc-HpdqgpHQ6y" rel="nofollow" target="_new">Underdog</a> runs best ball contests in every major sport, including season-long and playoff formats.</p> <p>Using industry-leading data and technology, Stokastic has leveraged as much information as possible in our Underdog Entry Builder tool to help users make the best decisions as possible.</p> <p>Pick'em contests are quickly becoming popular among sports bettors who don’t have access to legalized gambling or simply enjoy the challenge of this format. Just like pick'em platforms such as <a href="https://www.stokastic.com/promos-bonuses/prizepicks/" target="_new">PrizePicks</a>, <a href="https://play.underdogfantasy.com/pc-HpdqgpHQ6y" rel="nofollow" target="_new">Underdog</a> has nuances that you’ll need to understand before getting in on the action. The differences include the option to make correlated picks and a slightly different hold percentage.</p> <h3>How Does Underdog Fantasy Work?</h3> <p>So, how does <a href="https://play.underdogfantasy.com/pc-HpdqgpHQ6y" rel="nofollow" target="_new">Underdog Fantasy</a> work? The site has five tiers to their pick'em contests, all of which have different potential ROIs if you’re playing correctly. The best ROI spot is the three-pick pick'em. That may seem counterintuitive since it requires you to get more selections right to win yet pays less than one with four or more picks. However, the payout structure and the winning percentage you’ll need for your three-pick plays make it the safest way to go on <a href="https://play.underdogfantasy.com/pc-HpdqgpHQ6y" rel="nofollow" target="_new">Underdog</a>.</p> <p>If you’re looking to learn more about Underdog Fantasy, Stokastic has you covered. Here’s advice on <a href="/articles/betting-101/how-to-play-underdog-fantasy-pick-ems-finding-value-in-multiplier-contests-y10">how you can play Underdog Fantasy</a>; and our post on <a href="/articles/betting-101/underdog-fantasy-free-squares-explained-when-to-find-them-and-how-to-use-them-y10">how Underdog Fantasy free squares operate</a>.</p>`;